<template>
  <transition-group 
    name="fade" 
    tag="div" 
    class="fixed top-4 left-1/2 transform -translate-x-1/2 z-50 space-y-2"
  >
    <div
      v-for="notification in notificationStore.notifications"
      :key="notification.id"
      :class="getNotificationClasses(notification.type)"
      class="max-w-md w-full shadow-lg rounded-lg pointer-events-auto ring-1 ring-black ring-opacity-5 overflow-hidden"
    >
      <div class="p-4" style="min-width: 28rem;">
        <div class="flex items-start">
          <div class="flex-shrink-0">
            <component :is="getIcon(notification.type)" :class="getIconClasses(notification.type)" class="w-6 h-6" />
          </div>
          <div class="ml-3 w-0 flex-1 pt-0.5">
            <p class="text-sm font-medium" :class="getTitleClasses(notification.type)">
              {{ notification.title }}
            </p>
            <p v-if="notification.message" class="mt-1 text-sm" :class="getMessageClasses(notification.type)">
              {{ notification.message }}
            </p>
          </div>
          <div class="ml-4 flex-shrink-0 flex">
            <button
              @click="notificationStore.removeNotification(notification.id)"
              :class="getCloseButtonClasses(notification.type)"
              class="rounded-md inline-flex text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2"
            >
              <span class="sr-only">Close</span>
              <X class="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>
    </div>
  </transition-group>
</template>

<script setup lang="ts">
import { 
  CheckCircle, 
  XCircle, 
  AlertTriangle, 
  Info, 
  X 
} from 'lucide-vue-next'
import { useNotificationStore } from '@/stores/notification'
import type { NotificationMessage } from '@/types'

const notificationStore = useNotificationStore()

const getIcon = (type: NotificationMessage['type']) => {
  switch (type) {
    case 'success': return CheckCircle
    case 'error': return XCircle
    case 'warning': return AlertTriangle
    case 'info': return Info
    default: return Info
  }
}

const getNotificationClasses = (type: NotificationMessage['type']): string => {
  switch (type) {
    case 'success': return 'bg-white border-l-4 border-green-400'
    case 'error': return 'bg-white border-l-4 border-red-400'
    case 'warning': return 'bg-white border-l-4 border-yellow-400'
    case 'info': return 'bg-white border-l-4 border-blue-400'
    default: return 'bg-white border-l-4 border-gray-400'
  }
}

const getIconClasses = (type: NotificationMessage['type']): string => {
  switch (type) {
    case 'success': return 'text-green-400'
    case 'error': return 'text-red-400'
    case 'warning': return 'text-yellow-400'
    case 'info': return 'text-blue-400'
    default: return 'text-gray-400'
  }
}

const getTitleClasses = (type: NotificationMessage['type']): string => {
  switch (type) {
    case 'success': return 'text-green-900'
    case 'error': return 'text-red-900'
    case 'warning': return 'text-yellow-900'
    case 'info': return 'text-blue-900'
    default: return 'text-gray-900'
  }
}

const getMessageClasses = (type: NotificationMessage['type']): string => {
  switch (type) {
    case 'success': return 'text-green-700'
    case 'error': return 'text-red-700'
    case 'warning': return 'text-yellow-700'
    case 'info': return 'text-blue-700'
    default: return 'text-gray-700'
  }
}

const getCloseButtonClasses = (type: NotificationMessage['type']): string => {
  switch (type) {
    case 'success': return 'focus:ring-green-500'
    case 'error': return 'focus:ring-red-500'
    case 'warning': return 'focus:ring-yellow-500'
    case 'info': return 'focus:ring-blue-500'
    default: return 'focus:ring-gray-500'
  }
}
</script>
